import type { SVGProps } from 'react';
import classNames from 'classnames';
import type { IconSize } from '../Icon';
import ratingIcons, { type ParentalRatingRegion, type TokensForRegion } from '../assets/ratings';
import './ParentalRatingIcon.scss';

const RATING_ICON_PREFIX = 'rating-';

/**
 * Props for the `ParentalRatingIcon` component.
 *
 * When `region` is specified, `token` is constrained to valid tokens for that
 * region (see `TokensForRegion<R>`), giving better autocomplete and
 * compile-time checks. The component still returns null at runtime if no icon
 * exists for the combination.
 *
 * Extends standard SVG props so consumers can pass data-testid, onClick, role,
 * aria-*, etc. Conflicting props (className) are omitted and re-declared.
 *
 * Note: `color` is intentionally not supported; rating icons use their predefined artwork.
 */
export interface ParentalRatingIconProps<R extends ParentalRatingRegion = ParentalRatingRegion>
  extends Omit<SVGProps<SVGSVGElement>, 'className'> {
  /** Region code used to resolve the rating icon (for example, `au`, `uk`, or `mx-latam`). */
  region: R;
  /** Rating token for the chosen region (for example, `g`, `pg-13`, or `15-plus`). Constrained by region for type safety. */
  token: TokensForRegion<R>;
  /** Size of the icon; defaults to `medium` so the icon does not render at the SVG's large intrinsic dimensions. */
  size?: IconSize;
  /** Accessible label for screen readers; when omitted, the icon is treated as decorative (aria-hidden). */
  label?: string;
  className?: string;
}

/**
 * Renders a parental rating icon by region and token. Combines `region` and `token` into a
 * concrete rating icon name. Returns null when no icon exists for the combination.
 *
 * @param props - Component props.
 * @param props.region - Region code used to resolve the rating icon (for example, `au`, `uk`, or `mx-latam`).
 * @param props.token - Rating token within the given region (for example, `g`, `pg-13`, or `15-plus`). Typed per region for autocomplete.
 * @param props.size - Size of the icon; defaults to `medium` so the icon does not render at the SVG's large intrinsic dimensions.
 * @param props.label - Accessible label for screen readers; when omitted, the icon is treated as decorative (aria-hidden).
 * @param props.className - Additional class name(s) for the SVG element.
 */
export function ParentalRatingIcon<R extends ParentalRatingRegion = ParentalRatingRegion>({
  region,
  token,
  size = 'medium',
  label,
  className,
  ...rest
}: ParentalRatingIconProps<R>): JSX.Element | null {
  const iconName = `${RATING_ICON_PREFIX}${region}-${token}`;
  const RatingSvg = ratingIcons[iconName as keyof typeof ratingIcons];
  if (!RatingSvg) return null;
  return (
    <RatingSvg
      className={classNames(
        'vds-parental-rating-icon',
        `vds-parental-rating-icon--${size}`,
        className,
      )}
      aria-label={label}
      aria-hidden={!label}
      {...rest}
    />
  );
}
